import { MOCK_TASK_LIST_DATA } from './tasks.mock';
import type {
  Task,
  TaskApiParams,
  TaskGroupResult,
  TaskQueryResult,
  TaskSummaryDto,
} from '../types';

const DELAY_MS = 300;

function delay(ms: number) {
  return new Promise<void>((resolve) => setTimeout(resolve, ms));
}

// Cursor encoding: base64(JSON.stringify({ offset: N }))
function encodeCursor(offset: number): string {
  return btoa(JSON.stringify({ offset }));
}

function decodeCursor(cursor: string | undefined): number {
  if (!cursor) return 0;
  try {
    const parsed: unknown = JSON.parse(atob(cursor));
    if (typeof parsed === 'object' && parsed !== null && 'offset' in parsed) {
      return Number((parsed as { offset: unknown }).offset) || 0;
    }
    return 0;
  } catch {
    return 0;
  }
}

function taskToSummaryDto(task: Task, projectId: string): TaskSummaryDto {
  const priorityInt: Record<string, number> = { urgent: 4, high: 3, medium: 2, low: 1 };
  return {
    id: task.id,
    tagIds: task.tagIds ?? [],
    project: { id: projectId, name: 'Mock Project', color: '#4F46E5' },
    board: { id: 'board-mock', name: 'Sprint 1' },
    status: {
      id: task.status.id,
      name: task.status.name,
      color: task.status.color,
      type: task.status.type,
    },
    group: task.groupId
      ? {
          id: task.groupId,
          name: MOCK_TASK_LIST_DATA.groups.find((g) => g.id === task.groupId)?.name ?? task.groupId,
          description: null,
        }
      : null,
    parent: task.parentId ? { id: task.parentId, title: null } : null,
    subtaskCount: task.subtaskCount ?? 0,
    title: task.title,
    priority: task.priority ? (priorityInt[task.priority] ?? 0) : 0,
    assignee: task.assignee
      ? {
          id: task.assignee.id,
          displayName: task.assignee.name,
          avatarUrl: task.assignee.avatarUrl ?? null,
          email: null,
        }
      : null,
    createdBy: null,
    startDate: task.startDate ?? null,
    dueDate: task.dueDate ?? null,
    estimatedHours: task.estimatedMinutes ? task.estimatedMinutes / 60 : null,
    totalTrackedHours: task.totalTrackedHours ?? 0,
    isCompleted: task.isCompleted,
    order: task.order,
    createdAt: new Date(Date.now() - 86400000).toISOString(),
    updatedAt: null,
  };
}

export async function getMockQueryResult(
  projectId: string,
  params: TaskApiParams,
): Promise<TaskQueryResult> {
  await delay(DELAY_MS);

  const limit = params.limit ?? 50;
  const offset = decodeCursor(params.cursor);
  let tasks = MOCK_TASK_LIST_DATA.tasks.map((t) => taskToSummaryDto(t, projectId));

  // Apply search
  if (params.search) {
    const q = params.search.toLowerCase();
    tasks = tasks.filter((t) => t.title.toLowerCase().includes(q));
  }

  const useGrouping = params.groupBy && params.groupBy !== 'none';

  if (useGrouping || params.view === 'kanban') {
    const groupField = params.groupBy ?? 'status';

    const groupMap = new Map<
      string,
      { label: string; color: string | null; items: TaskSummaryDto[] }
    >();

    for (const task of tasks) {
      let key = '';
      let label = '';
      let color: string | null = null;

      if (groupField === 'status') {
        key = task.status?.id ?? '';
        label = task.status?.name ?? '';
        color = task.status?.color ?? null;
      } else if (groupField === 'priority') {
        key = String(task.priority ?? 0);
        const labels: Record<string, string> = {
          '4': 'Urgent',
          '3': 'High',
          '2': 'Medium',
          '1': 'Low',
          '0': 'None',
        };
        label = labels[key] ?? key;
        const colors: Record<string, string> = {
          '4': '#EF4444',
          '3': '#F97316',
          '2': '#F59E0B',
          '1': '#3B82F6',
          '0': '#94A3B8',
        };
        color = colors[key] ?? null;
      } else if (groupField === 'assignee') {
        key = task.assignee?.id ?? '';
        label = task.assignee?.displayName ?? 'Unassigned';
        color = null;
      } else if (groupField === 'group') {
        key = task.group?.id ?? '';
        label = task.group?.name ?? 'No group';
        color = null;
      }

      // If groupKey is set, only return that one group
      if (params.groupKey !== undefined && key !== params.groupKey) continue;

      if (!groupMap.has(key)) groupMap.set(key, { label, color, items: [] });
      groupMap.get(key)!.items.push(task);
    }

    const groups: TaskGroupResult[] = [];
    for (const [key, { label, color, items }] of groupMap) {
      const groupOffset = params.groupKey !== undefined ? offset : 0;
      const slice = items.slice(groupOffset, groupOffset + limit);
      const remaining = items.length - groupOffset - limit;
      groups.push({
        key,
        label,
        color,
        count: items.length,
        items: slice,
        nextCursor: remaining > 0 ? encodeCursor(groupOffset + limit) : null,
      });
    }

    return { items: [], nextCursor: null, total: 0, groups };
  }

  // Flat response
  const total = tasks.length;
  const slice = tasks.slice(offset, offset + limit);
  const remaining = total - offset - limit;

  return {
    items: slice,
    nextCursor: remaining > 0 ? encodeCursor(offset + limit) : null,
    total,
    groups: [],
  };
}
